import {
	askChatGptProductDaily,
	rateCustomerMessageChat,
	transcript,
	getResponseForImage,
} from '@/api/query/workflow-daily';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { DemoConversationContext } from '@/common/contexts/DemoConversation';
import { TALKING_ROLES } from './TalkiMessage';
import Animation1 from '@/common/images/animation-1.gif';
import { typeWriter } from '@/common/functions/typing';
import { IconWrapper } from './TalkingMicrofon.styles';
import { FROM_SOURCE_TYPES } from '@/common/constants/types';
import MessageBar from '@/common/components/chat/MessageBar';
import HintBoxMessage from './HintBoxMessage';
import ScoreBoxMessage from './ScoreBoxMessage';
import AudioRecordingContextProvider from '@/common/contexts/AudioRecordingContext';
import { VALID_PATHS } from '@/common/constants/validPaths';
import { voiceRunner } from '@/common/functions/voiceRunner';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { CustomerDataContext } from '@/common/components/layout/productLayout/context/CustomerDataContext';

const TalkingMicrofon = ({
	setCurrent,
	current,
	setConversationWithChatGPT,
	setMessageScore,
	conversationWithChatGPT,
	setShowBadMessage,
	showBadMessage,
	hintData,
	setHintData,
}) => {
	const [showHints, setShowHints] = useState(false);
	const [hints, setHints] = useState([]);
	const [speechLasts, setSpeechLasts] = useState();

	const { isActiveHelpFeaturesTest } = useContext(CustomerDataContext);
	const {
		showLoading,
		dailyConversation,
		muteVoice,
		showPopUpTutorial,
		setUserVoice,
	} = useContext(DemoConversationContext);

	const presenceChecker = useRef();

	const transcriptMutation = useMutation({
		mutationFn: (file) => transcript(file),
		onSuccess: ({ data }) => {
			if (!data && data?.length === 0) return;

			const userMessageId = `user-message-id-${
				conversationWithChatGPT.length + 1
			}`;

			setConversationWithChatGPT({ role: TALKING_ROLES.USER, message: data });

			const payload = {};
			payload.message = data;
			payload._id = dailyConversation._id;
			payload.source = FROM_SOURCE_TYPES.SPEAK;
			payload.userMessageId = userMessageId;

			rateUserMessageMutation.mutate(payload);
			getResponseForChatGptMutation.mutate(payload);
		},
	});

	const rateUserMessageMutation = useMutation({
		mutationFn: (value) => rateCustomerMessageChat(value),
		onSuccess: ({ data }) => {
			setMessageScore(data);
		},
	});

	const handleResponseFromChatGpt = async (data) => {
		let parsedText = null;

		if (!muteVoice) {
			await voiceRunner(
				data.speaking.data,
				'',
				setSpeechLasts,
				VALID_PATHS.DAILY,
			);
		}

		typeWriter({
			text: parsedText ?? data.text,
			setText: setCurrent,
			typing: 45,
			onEnd: () => {
				setCurrent(null);
				setSpeechLasts(() => false);
				setConversationWithChatGPT({
					index: conversationWithChatGPT.length,
					role: TALKING_ROLES.BOT,
					message: data.text,
				});
			},
		});
	};

	const getResponseForImageMutation = useMutation({
		mutationFn: (v) => getResponseForImage(v),
		onSuccess: ({ data }) => {
			setConversationWithChatGPT({
				index: conversationWithChatGPT.length,
				role: TALKING_ROLES.USER,
				imageId: data?.image?._id,
				nsfw: data?.nsfw,
			});

			handleResponseFromChatGpt(data);
		},
	});

	const getResponseForChatGptMutation = useMutation({
		mutationFn: (value) => askChatGptProductDaily(value),
		onSuccess: ({ data }) => handleResponseFromChatGpt(data),
	});

	const handleSendMessage = (messageInput, hint = false) => {
		if (!messageInput) return;

		setHintData(null);

		const message = messageInput;
		const userMessageId = `user-message-id-${
			conversationWithChatGPT.length + 1
		}`;

		setConversationWithChatGPT({ role: TALKING_ROLES.USER, message: message });

		const payload = {};
		payload.message = message;
		payload._id = dailyConversation._id;
		payload.source = hint ? FROM_SOURCE_TYPES.HINT : FROM_SOURCE_TYPES.WRTIE;
		payload.userMessageId = userMessageId;

		rateUserMessageMutation.mutate(payload);
		setTimeout(
			() => getResponseForChatGptMutation.mutate(payload),
			isActiveHelpFeaturesTest ? 4000 : 0,
		);
	};

	const handleSendImage = (image) => {
		if (!image) return;

		const userMessageId = `user-message-id-${
			conversationWithChatGPT.length + 1
		}`;

		const payload = {};
		payload.image = image;
		payload.conversationId = dailyConversation._id;
		payload.source = FROM_SOURCE_TYPES.IMAGE;
		payload.userMessageId = userMessageId;

		getResponseForImageMutation.mutate(payload);
	};

	const isResponding =
		speechLasts ||
		getResponseForChatGptMutation.isLoading ||
		transcriptMutation.isLoading ||
		getResponseForChatGptMutation.isLoading;

	const handleHints = () => {
		if (showHints) {
			return setShowHints(false);
		}

		setShowHints(true);
	};

	useEffect(() => {
		if (
			dailyConversation &&
			showPopUpTutorial === false &&
			conversationWithChatGPT.length === 0 &&
			!current
		) {
			const payload = {};
			payload._id = dailyConversation._id;

			getResponseForChatGptMutation.mutate(payload);
		}
	}, [dailyConversation, showPopUpTutorial]);

	useEffect(() => {
		const element = document.getElementById(`bottom-info`);
		element?.scrollIntoView({ behavior: 'smooth' });
	}, [
		transcriptMutation?.isLoading,
		getResponseForChatGptMutation?.isLoading,
		getResponseForImageMutation.isLoading,
	]);

	return (
		<>
			{hintData && !isActiveHelpFeaturesTest && (
				<HintBoxMessage
					data={hintData}
					setClose={() => setHintData(null)}
					handleProccessHint={() => {
						setHintData(null);
						handleSendMessage(hintData.hint_result, true);
					}}
				/>
			)}
			{showBadMessage && (
				<ScoreBoxMessage
					data={showBadMessage}
					setClose={() => setShowBadMessage(null)}
				/>
			)}
			{getResponseForImageMutation.isLoading && (
				<div style={{ textAlign: 'right', marginRight: 10, marginTop: 10 }}>
					<Skeleton style={{ width: 200, height: 300 }} />
				</div>
			)}
			{transcriptMutation.isLoading && (
				<div style={{ textAlign: 'right', minHeight: '37px' }}>
					<img
						alt=''
						src={Animation1}
						style={{
							width: '37px',
							height: '37px',
							marginRight: '20px',
							marginTop: '10px',
						}}
					/>
				</div>
			)}
			{getResponseForChatGptMutation.isLoading && (
				<div style={{ textAlign: 'left', minHeight: '37px' }}>
					<img
						alt=''
						src={Animation1}
						style={{ width: '37px', height: '37px' }}
					/>
				</div>
			)}
			{showLoading && (
				<div
					style={{
						textAlign: 'center',
						marginTop: '-40px',
						marginLeft: '-80px',
					}}
				>
					<img alt='' src={Animation1} style={{ width: '120px' }} />
				</div>
			)}
			<div ref={presenceChecker} />
			<IconWrapper>
				<AudioRecordingContextProvider>
					<MessageBar
						isBlock={
							getResponseForChatGptMutation.isLoading ||
							transcriptMutation.isLoading
						}
						sendText={handleSendMessage}
						sendVoice={(data) => {
							setUserVoice(() => data.get('file'));
							transcriptMutation.mutate(data);
						}}
						sendImage={handleSendImage}
					/>
				</AudioRecordingContextProvider>
			</IconWrapper>
		</>
	);
};

export default TalkingMicrofon;
